import { EventEmitter } from 'events';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  type AttributeFilters,
  type GraphInitialParameters,
  type PathLookupGraphSettings,
  type SnapshotId,
  type TopologyGraphSettings,
  isGraphSharedViewParameters,
} from '@ipfabric/utils';
import { ___, partialApply } from '@ipfabric/utils/fp/partialApply';
import { prop } from '@ipfabric/utils/fp/prop';
import type { DeviceViewModel } from 'components/DeviceView/model/DeviceViewModel';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { type GraphState, createGraphState } from 'components/Graph/GraphState';
import { loadEmptyGraph } from 'components/Graph/commands/loadEmptyGraph';
import { loadGraph } from 'components/Graph/commands/loadGraph';
import { loadGraphInitialData } from 'components/Graph/commands/loadGraphInitialData';
import { setAppliedPreset } from 'components/Graph/commands/settings/setAppliedPreset';
import type { GraphCommandContext } from 'components/Graph/model/GraphCommandContext';
import type { GraphCommandEvents } from 'components/Graph/model/GraphCommandEvents';
import type { GraphSingletonController } from 'components/Graph/model/GraphSingletonController';
import { useLocale } from 'containers/App/LocaleContext';
import { useLogError } from 'containers/ErrorBoundary/useLogError';
import { pipe } from 'fp-ts/function';
import type { Snapshot } from 'pages/SnapshotManagement/snapshotManagementTypes';
import { useSnapshots } from 'pages/SnapshotManagement/useSnapshots';
import { type Ref, useEffect, useMemo, useRef, useState } from 'react';
import { createSingletonControllerCollection } from 'utils/abortRequest/SingletonController';
import type { TypedEventEmitter } from 'utils/events/TypedEventEmitter';
import { useNotifications } from 'utils/hooks/useNotifications';
import { usePermissionScope } from 'utils/hooks/usePermissionScope';
import { type StateProxy, useStateProxy } from 'utils/react/StateProxy';
import {
  type CommandDispatcher,
  useCommandDispatcher,
} from 'utils/react/useCommandDispatcher';
import { useApi } from 'utils/request/useApi';
import type { GraphAppHandleRef } from './GraphApp/plugins/externalApi';
import { getPresetDraftFunctionByMode } from './commands/helpers/getPresetFunctionByMode';
import { loadSharedView } from './commands/loadSharedView';
import { mergeSettingsToPreset } from './helpers/mergeSettingsToPreset';
import { emptyGraphData } from './model/GraphData';
import { useGraphDeviceView } from './useGraphDeviceView';

export interface GraphHook extends GraphState {
  deviceView: DeviceViewModel;
  deviceViewContainer: Ref<HTMLDivElement>;
  dispatch: CommandDispatcher<GraphCommandContext>;
  graphAppHandleRef: GraphAppHandleRef;
  initialParameters: GraphInitialParameters | null;
}

/**
 * Load stuff that should be loaded only once on mount
 *
 * @return boolean true when all the resources are loaded.
 */
const useGraphInitialData = (
  dispatch: CommandDispatcher<GraphCommandContext>,
  [getState]: StateProxy<GraphState>,
): boolean => {
  const [resourcesLoaded, setResourcesLoaded] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: just initializer
  useEffect(() => {
    (async () => {
      await dispatch(loadGraphInitialData);
      setResourcesLoaded(true);
    })();
  }, []);
  return resourcesLoaded && getState().layoutSettings.presets.length > 0;
};

const loadNewGraph = async (
  dispatch: CommandDispatcher<GraphCommandContext>,
  initialParameters: GraphInitialParameters | null,
  loadedSnapshots: Snapshot[],
) => {
  if (isGraphSharedViewParameters(initialParameters)) {
    await dispatch(loadSharedView, {
      parameters: initialParameters,
      loadedSnapshots,
    });
  } else if (initialParameters === null) {
    dispatch(loadEmptyGraph);
  } else {
    await dispatch(loadGraph, {
      overlay: 'none',
      parameters: initialParameters,
      transferPreviousState: false,
    });
  }
};

const useReloadGraphOnPropsChange = (props: {
  dispatch: CommandDispatcher<GraphCommandContext>;
  initialDataLoaded: boolean;
  initialParameters: GraphInitialParameters | null;
  initialSettings: TopologyGraphSettings | PathLookupGraphSettings | null;
  snapshotId: SnapshotId;
  stateProxy: StateProxy<GraphState>;
  attributeFilters: AttributeFilters;
}) => {
  const [getState, updateState] = props.stateProxy;
  const lastUsedParameters = useRef<GraphInitialParameters | null>();
  const loadedSnapshots = useSnapshots();

  // biome-ignore lint/correctness/useExhaustiveDependencies: update graph only when given props change
  useEffect(() => {
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD cleanup eventually
    (async () => {
      updateState((draft) => {
        draft.snapshotId = props.snapshotId;
        draft.attributeFilters = props.attributeFilters;
      });
      if (!props.initialDataLoaded) {
        return;
      }

      if (props.initialParameters === lastUsedParameters.current) {
        // this branch can be reached only when only snapshotId changes:
        // - After initialDataLoaded becomes true, 'else' branch is always reached first, because
        //   lastUsedParameters.current is undefined which props.initialParameters can't ever be.
        // - This condition verifies that parameters did not change.
        // -> The only remaining field that could trigger this hook is snapshotId.
        // -> So, reload the graph if there is any in this case.
        if (getState().graphMetadata !== null) {
          try {
            await props.dispatch(loadGraph, {
              overlay: 'keep',
            });
          } catch (error) {
            // If the graph fails to load, we need to reset the graph state to empty
            updateState((draft) => {
              draft.graph = emptyGraphData;
            });
            throw error;
          }
        }
      } else {
        lastUsedParameters.current = props.initialParameters;

        // Initial settings are applicable only for pathlookup/topology initial parameters while shared virew
        // already contains settings by default
        if (
          props.initialSettings &&
          props.initialParameters &&
          props.initialParameters.type !== 'sharedView'
        ) {
          pipe(
            getState(),
            getPresetDraftFunctionByMode[props.initialParameters.type],
            partialApply(mergeSettingsToPreset, props.initialSettings, ___),
            (preset) => props.dispatch(setAppliedPreset, { preset }),
          );
        }
        await loadNewGraph(
          props.dispatch,
          props.initialParameters,
          loadedSnapshots,
        );
      }
    })();
  }, [
    props.initialDataLoaded,
    props.initialParameters,
    props.snapshotId,
    props.attributeFilters,
  ]);
};

const useGraphSingletonControllers = () => {
  const controllers = useMemo(
    () => createSingletonControllerCollection<GraphSingletonController>(),
    [],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: cleanup at the very end
  useEffect(
    () => () => {
      controllers.abortAll();
    },
    [],
  );
  return controllers;
};

export function useGraph(
  snapshotId: string,
  initialParameters: GraphInitialParameters | null,
  initialSettings: TopologyGraphSettings | PathLookupGraphSettings | null,
): GraphHook {
  const api = useApi();
  const logError = useLogError();
  const { showNotification } = useNotifications();
  const msg = useLocale();
  const graphAppHandleRef: GraphAppHandleRef = useRef(null);
  const permissionScope = usePermissionScope();
  const deviceViewContainer = useRef<HTMLDivElement>(null);
  const singletonControllers = useGraphSingletonControllers();
  const globalCtxProps = useGlobalStateContextSelectors({
    attributeFilters: prop('attributeFilters'),
  });

  const stateProxy = useStateProxy<GraphState>(() =>
    createGraphState({
      snapshotId,
      attributeFilters: globalCtxProps.attributeFilters,
    }),
  );
  const [getState, updateState] = stateProxy;

  const getDeviceView = useStableCallback(() => deviceView);

  const eventEmitter: TypedEventEmitter<GraphCommandEvents> = useMemo(
    () => new EventEmitter(),
    [],
  );

  const graphCommandContext: GraphCommandContext = useMemo(
    () => ({
      api,
      eventEmitter,
      getDeviceView,
      getState,
      graphAppHandleRef,
      logError,
      msg,
      permissionScope,
      singletonControllers,
      updateState,
      showNotification,
    }),
    [
      eventEmitter,
      getState,
      msg,
      permissionScope,
      singletonControllers,
      updateState,
    ],
  );
  const dispatch = useCommandDispatcher(graphCommandContext);

  const deviceView = useGraphDeviceView({
    deviceViewContainer,
    dispatch,
    graph: getState(),
  });

  const initialDataLoaded = useGraphInitialData(dispatch, stateProxy);

  useReloadGraphOnPropsChange({
    dispatch,
    initialDataLoaded,
    initialParameters,
    initialSettings,
    snapshotId,
    stateProxy,
    attributeFilters: globalCtxProps.attributeFilters,
  });

  const state = getState();
  return useMemo(
    () => ({
      deviceViewContainer,
      deviceView,
      dispatch,
      graphAppHandleRef,
      initialParameters,
      ...state,
    }),
    [deviceView, state, initialParameters],
  );
}
